/**
 * LeagueSettings (Checkpoint 4)
 * Commissioner-only schema editor.
 * Tabs: General / Roster / Scoring / Waivers / Trades / Playoffs.
 * Structural fields lock once season is ACTIVE; locked fields render disabled
 * with a tooltip explaining why.
 *
 * Calls existing PUT /api/leagues/{id}/settings (idempotent merge with defaults).
 * Triggers POST /api/season/{id}/recompute-scores when scoring_format changes
 * mid-season (only available pre-lock; gating handled server-side anyway).
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Save, Lock, RotateCcw, AlertCircle, CheckCircle2 } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth, authHeaders } from "../contexts/AuthContext";
import { useSeason } from "../contexts/SeasonContext";
import SeasonShell from "../components/season/SeasonShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "general", label: "General" },
  { id: "roster", label: "Roster" },
  { id: "scoring", label: "Scoring" },
  { id: "waivers", label: "Waivers" },
  { id: "trades", label: "Trades" },
  { id: "playoffs", label: "Playoffs" },
  { id: "divisions", label: "Divisions" },
  // [Phase 2c-D §6.7 NEW-9] Mock-only league deletion lives here.
  { id: "danger", label: "Danger Zone" },
];

// Fields that lock once the league is in SEASON_ACTIVE / PLAYOFFS
const STRUCTURAL_KEYS = new Set([
  "roster", "roster_size", "scoring_format",
  "waiver_type", "regular_season_weeks",
  "playoff_weeks", "playoff_teams", "divisions",
]);

export default function LeagueSettings() {
  const { id: leagueId } = useParams();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { seasonInfo } = useSeason();

  const [tab, setTab] = useState("general");
  const [league, setLeague] = useState(null);
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null); // working copy
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {ok, text}
  const [recomputeBusy, setRecomputeBusy] = useState(false);

  useEffect(() => {
    if (!leagueId) return;
    axios.get(`${API}/leagues/${leagueId}`, { headers: authHeaders() })
      .then((r) => {
        setLeague(r.data);
        const s = r.data.settings || {};
        setSettings(s);
        setDraft(JSON.parse(JSON.stringify(s)));
      })
      .catch((e) => setMsg({ ok: false, text: e?.response?.data?.detail || "Failed to load league" }));
  }, [leagueId]);

  // Determine if structural fields are locked
  const seasonStarted = seasonInfo && ["SEASON_ACTIVE", "PLAYOFFS", "SEASON_COMPLETE"].includes(seasonInfo.status);
  const isCommish = league && user && league.commissioner_id === user.id;
  const dirty = settings && draft && JSON.stringify(settings) !== JSON.stringify(draft);

  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-white/[0.08]" : "border-black/[0.08]";
  const text = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const muted = isDark ? "text-[rgba(255,255,255,0.45)]" : "text-[rgba(0,0,0,0.50)]";
  const inputBase = isDark
    ? "bg-[#0A0A0A] border-white/[0.10] focus:border-[#FF6B00] text-[#F5F5F5]"
    : "bg-white border-black/[0.10] focus:border-[#FF6B00] text-[#0A0A0A]";

  const set = (key, val) => setDraft((d) => ({ ...d, [key]: val }));
  const setRoster = (k, v) =>
    setDraft((d) => ({ ...d, roster: { ...(d.roster || {}), [k]: parseInt(v, 10) || 0 } }));

  const onSave = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await axios.put(
        `${API}/leagues/${leagueId}/settings`,
        { settings: draft }, { headers: authHeaders() },
      );
      setSettings(r.data.settings);
      setDraft(JSON.parse(JSON.stringify(r.data.settings)));
      setMsg({ ok: true, text: "Settings saved." });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Save failed" });
    } finally { setBusy(false); }
  };

  const onRecompute = async () => {
    if (!window.confirm("Recompute all weekly scores using current scoring format? Standings will rebuild.")) return;
    setRecomputeBusy(true); setMsg(null);
    try {
      const r = await axios.post(
        `${API}/season/${leagueId}/recompute-scores`, {},
        { headers: authHeaders() },
      );
      setMsg({ ok: true, text: `Scores recomputed for ${r.data.weeks_recomputed} weeks (${r.data.scores_rebuilt} entries).` });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Recompute failed" });
    } finally { setRecomputeBusy(false); }
  };

  if (!league || !draft) {
    return <SeasonShell><div className="py-20 text-center font-display font-black text-2xl text-[#FF6B00]">Loading…</div></SeasonShell>;
  }
  if (!isCommish) {
    return (
      <SeasonShell>
        <div className={`${cardBg} border ${cardBorder} rounded-2xl p-6 text-center`} data-testid="settings-not-commish">
          <Lock size={28} className="text-[#FF6B00] mx-auto mb-2" />
          <h2 className={`font-display font-black text-2xl uppercase ${text}`}>Commissioner Only</h2>
          <p className={`font-body text-sm mt-1 ${muted}`}>Only the league commissioner can edit settings.</p>
        </div>
      </SeasonShell>
    );
  }

  return (
    <SeasonShell>
      <div data-testid="settings-page">
        {/* Header + Save bar */}
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <h1 className={`font-display font-black uppercase tracking-wide text-3xl ${text}`}>League Settings</h1>
            <p className={`font-body text-sm ${muted}`}>{league.name}{seasonStarted && " · season active - structural fields locked"}</p>
          </div>
          <div className="flex items-center gap-2">
            {dirty && (
              <button
                onClick={() => setDraft(JSON.parse(JSON.stringify(settings)))}
                data-testid="settings-discard-btn"
                className={`px-3 py-2 rounded-xl border font-body text-sm ${isDark ? "border-white/[0.10] text-white/70" : "border-black/[0.15] text-black/70"}`}
              >
                Discard
              </button>
            )}
            <button
              onClick={onSave}
              disabled={busy || !dirty}
              data-testid="settings-save-btn"
              className="px-4 py-2 rounded-xl bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-50 text-white font-display font-black uppercase text-sm tracking-wide flex items-center gap-2"
            >
              <Save size={14} />{busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {msg && (
          <div className={`mb-4 px-4 py-3 rounded-xl border flex items-center gap-2 ${msg.ok ? "bg-[#00C853]/10 border-[#00C853]/40 text-[#00C853]" : "bg-[#FF4444]/10 border-[#FF4444]/40 text-[#FF4444]"}`} data-testid="settings-msg">
            {msg.ok ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
            <span className="font-body text-sm">{msg.text}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/[0.08] mb-5 overflow-x-auto" data-testid="settings-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`settings-tab-${t.id}`}
              className={`px-4 py-2 font-display font-bold uppercase tracking-wide text-sm whitespace-nowrap ${tab === t.id ? "text-[#FF6B00] border-b-2 border-[#FF6B00] -mb-px" : muted}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab body */}
        <div className={`${cardBg} border ${cardBorder} rounded-2xl p-5 space-y-5`}>
          {tab === "general" && <GeneralTab draft={draft} set={set} muted={muted} text={text} inputBase={inputBase} />}
          {tab === "roster" && <RosterTab draft={draft} setRoster={setRoster} seasonStarted={seasonStarted} muted={muted} text={text} inputBase={inputBase} />}
          {tab === "scoring" && (
            <ScoringTab
              draft={draft} set={set} seasonStarted={seasonStarted}
              recomputeBusy={recomputeBusy} onRecompute={onRecompute}
              muted={muted} text={text} inputBase={inputBase}
            />
          )}
          {tab === "waivers" && <WaiverTab draft={draft} set={set} seasonStarted={seasonStarted} muted={muted} text={text} inputBase={inputBase} />}
          {tab === "trades" && <TradeTab draft={draft} set={set} muted={muted} text={text} inputBase={inputBase} />}
          {tab === "playoffs" && <PlayoffTab draft={draft} set={set} seasonStarted={seasonStarted} muted={muted} text={text} inputBase={inputBase} />}
          {tab === "divisions" && <DivisionsTab draft={draft} set={set} seasonStarted={seasonStarted} league={league} muted={muted} text={text} inputBase={inputBase} />}
          {tab === "danger" && <DangerZoneTab league={league} muted={muted} text={text} />}
        </div>
      </div>
    </SeasonShell>
  );
}

// ─── Reusable bits ──────────────────────────────────────────────────────
function FieldRow({ label, locked, lockReason, children, muted, text }) {
  return (
    <div>
      <div className={`flex items-center justify-between mb-1.5`}>
        <label className={`font-body text-sm font-medium ${text}`}>{label}</label>
        {locked && (
          <span className={`flex items-center gap-1 text-[10px] uppercase tracking-widest ${muted}`} title={lockReason}>
            <Lock size={10} /> Locked
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function NumberField({ value, onChange, locked, inputBase, testid, min = 0, max = 99 }) {
  return (
    <input
      type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      disabled={locked} data-testid={testid} min={min} max={max}
      className={`w-full border rounded-lg px-3 py-2 font-body text-sm outline-none transition-colors disabled:opacity-50 ${inputBase}`}
    />
  );
}

function SelectField({ value, onChange, options, locked, inputBase, testid }) {
  return (
    <select
      value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      disabled={locked} data-testid={testid}
      className={`w-full border rounded-lg px-3 py-2 font-body text-sm outline-none transition-colors disabled:opacity-50 ${inputBase}`}
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ─── Tabs ────────────────────────────────────────────────────────────────
function GeneralTab({ draft, set, muted, text, inputBase }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <FieldRow label="League Name" muted={muted} text={text}>
        <input
          value={draft.name || ""} disabled
          className={`w-full border rounded-lg px-3 py-2 font-body text-sm outline-none opacity-60 ${inputBase}`}
        />
        <p className={`text-[10px] mt-1 ${muted}`}>Edit from the League dashboard.</p>
      </FieldRow>
      <FieldRow label="Season Year" muted={muted} text={text}>
        <NumberField value={draft.season_year || 2026} onChange={(v) => set("season_year", parseInt(v, 10) || 2026)} inputBase={inputBase} testid="settings-season-year" min={2020} max={2099} />
      </FieldRow>
      <FieldRow label="Auction Budget ($)" muted={muted} text={text}>
        <NumberField value={draft.budget ?? 200} onChange={(v) => set("budget", parseInt(v, 10) || 200)} inputBase={inputBase} testid="settings-budget" min={50} max={1000} />
      </FieldRow>
      <FieldRow label="Number of Teams" muted={muted} text={text}>
        <NumberField value={draft.num_teams ?? 12} onChange={(v) => set("num_teams", parseInt(v, 10) || 12)} inputBase={inputBase} testid="settings-num-teams" min={4} max={20} />
      </FieldRow>
    </div>
  );
}

function RosterTab({ draft, setRoster, seasonStarted, muted, text, inputBase }) {
  const r = draft.roster || { qb: 1, rb: 2, wr: 2, te: 1, flex: 1, k: 1, def: 1, bench: 5 };
  const total = Object.values(r).reduce((a, b) => a + (parseInt(b, 10) || 0), 0);
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          // Third element = per-slot ceiling. Bench goes to 99 (deep
          // dynasty/best-ball benches); starting slots stay at 9. Roster size
          // below — and the draft's ROUND COUNT — is the sum, so the bench
          // ceiling is what governs how long a draft runs.
          ["qb", "QB"], ["rb", "RB"], ["wr", "WR"], ["te", "TE"],
          ["flex", "FLEX"], ["k", "K"], ["def", "DEF"], ["bench", "Bench", 99],
        ].map(([k, label, max]) => (
          <FieldRow key={k} label={label} locked={seasonStarted} lockReason="Roster locked once season starts" muted={muted} text={text}>
            <NumberField
              value={r[k] ?? 0} onChange={(v) => setRoster(k, v)}
              locked={seasonStarted} inputBase={inputBase}
              testid={`settings-roster-${k}`} min={0} max={max ?? 9}
            />
          </FieldRow>
        ))}
      </div>
      <div className={`mt-4 px-3 py-2 rounded-lg ${muted} text-xs`} data-testid="settings-roster-total">
        Roster size: <span className="text-[#FF6B00] font-bold">{total}</span>
        <span className="ml-2 opacity-70">({total} draft rounds)</span>
      </div>
    </div>
  );
}

function ScoringTab({ draft, set, seasonStarted, recomputeBusy, onRecompute, muted, text, inputBase }) {
  return (
    <>
      <FieldRow
        label="Scoring Format"
        locked={seasonStarted}
        lockReason="Switch via Recompute Scores below"
        muted={muted} text={text}
      >
        <SelectField
          value={draft.scoring_format || "half_ppr"}
          onChange={(v) => set("scoring_format", v)}
          locked={seasonStarted}
          inputBase={inputBase}
          testid="settings-scoring-format"
          options={[
            { value: "standard", label: "Standard (No PPR)" },
            { value: "half_ppr", label: "Half PPR (0.5 / reception)" },
            { value: "full_ppr", label: "Full PPR (1.0 / reception)" },
          ]}
        />
      </FieldRow>

      {seasonStarted && (
        <div className={`mt-4 p-4 rounded-xl border ${muted} border-[#FF6B00]/30 bg-[#FF6B00]/5`}>
          <p className={`text-sm ${text} font-body mb-2`}>
            <strong>Mid-season scoring change:</strong> save the format above as a draft, then use <em>Recompute Scores</em> to rebuild every week's FantasyScore + matchup result + standings against the new rules.
          </p>
          <button
            onClick={onRecompute} disabled={recomputeBusy}
            data-testid="settings-recompute-btn"
            className="px-4 py-2 rounded-xl bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-50 text-white font-display font-black uppercase text-xs tracking-wide flex items-center gap-2"
          >
            <RotateCcw size={13} />{recomputeBusy ? "Recomputing…" : "Recompute Scores"}
          </button>
        </div>
      )}
    </>
  );
}

function WaiverTab({ draft, set, seasonStarted, muted, text, inputBase }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <FieldRow label="Waiver Type" locked={seasonStarted} lockReason="Cannot change waiver type mid-season" muted={muted} text={text}>
        <SelectField
          value={draft.waiver_type || "faab"}
          onChange={(v) => set("waiver_type", v)}
          locked={seasonStarted} inputBase={inputBase} testid="settings-waiver-type"
          options={[{ value: "faab", label: "FAAB (Blind Bid)" }, { value: "rolling", label: "Rolling Priority" }]}
        />
      </FieldRow>
      <FieldRow label="FAAB Budget ($)" muted={muted} text={text}>
        <NumberField value={draft.faab_budget ?? 100} onChange={(v) => set("faab_budget", parseInt(v, 10) || 100)} inputBase={inputBase} testid="settings-faab-budget" min={0} max={1000} />
      </FieldRow>
    </div>
  );
}

function TradeTab({ draft, set, muted, text, inputBase }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <FieldRow label="Trade Review Period (hours)" muted={muted} text={text}>
        <SelectField
          value={String(draft.trade_review_hours ?? 24)}
          onChange={(v) => set("trade_review_hours", parseInt(v, 10) || 0)}
          inputBase={inputBase} testid="settings-trade-review-hours"
          options={[{ value: "0", label: "Immediate" }, { value: "24", label: "24 hours" }, { value: "48", label: "48 hours" }]}
        />
      </FieldRow>
      <FieldRow label="Trade Deadline Week" muted={muted} text={text}>
        <NumberField value={draft.trade_deadline_week ?? 11} onChange={(v) => set("trade_deadline_week", parseInt(v, 10) || 0)} inputBase={inputBase} testid="settings-trade-deadline" min={0} max={17} />
      </FieldRow>
      <FieldRow label="Veto Threshold (votes)" muted={muted} text={text}>
        <NumberField value={draft.veto_threshold ?? 4} onChange={(v) => set("veto_threshold", parseInt(v, 10) || 0)} inputBase={inputBase} testid="settings-veto-threshold" min={0} max={20} />
        <p className={`text-[10px] mt-1 ${muted}`}>Set to 0 for commissioner-only veto.</p>
      </FieldRow>
    </div>
  );
}

function PlayoffTab({ draft, set, seasonStarted, muted, text, inputBase }) {
  const playoffTeams = parseInt(draft.playoff_teams ?? 6, 10) || 6;
  // Phase 1.5 [v1.1, B3] - 4-team brackets force playoff_weeks=2
  const playoffWeeksOptions = playoffTeams === 4
    ? [{ value: "2", label: "2 weeks (R1 + Championship)" }]
    : [
        { value: "2", label: "2 weeks" },
        { value: "3", label: "3 weeks" },
      ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <FieldRow label="Regular Season Length (weeks)" locked={seasonStarted} lockReason="Affects schedule generation" muted={muted} text={text}>
        <NumberField value={draft.regular_season_weeks ?? 14} onChange={(v) => set("regular_season_weeks", parseInt(v, 10) || 14)} locked={seasonStarted} inputBase={inputBase} testid="settings-regular-weeks" min={10} max={17} />
      </FieldRow>
      <FieldRow label="Playoff Teams" locked={seasonStarted} lockReason="Affects bracket sizing" muted={muted} text={text}>
        <SelectField
          value={String(playoffTeams)}
          onChange={(v) => {
            const newPt = parseInt(v, 10) || 6;
            set("playoff_teams", newPt);
            // [v1.1, B3] auto-snap playoff_weeks=2 when switching to 4-team
            if (newPt === 4 && parseInt(draft.playoff_weeks ?? 3, 10) !== 2) {
              set("playoff_weeks", 2);
            }
          }}
          locked={seasonStarted} inputBase={inputBase} testid="settings-playoff-teams"
          options={[{ value: "4", label: "4 teams" }, { value: "6", label: "6 teams" }, { value: "8", label: "8 teams" }]}
        />
      </FieldRow>
      <FieldRow
        label="Playoff Weeks"
        locked={seasonStarted || playoffTeams === 4}
        lockReason={playoffTeams === 4 ? "4-team bracket has only 2 rounds" : "Affects schedule generation"}
        muted={muted} text={text}
      >
        <SelectField
          value={String(draft.playoff_weeks ?? 3)}
          onChange={(v) => set("playoff_weeks", parseInt(v, 10) || 3)}
          locked={seasonStarted || playoffTeams === 4}
          inputBase={inputBase} testid="settings-playoff-weeks"
          options={playoffWeeksOptions}
        />
      </FieldRow>
      <FieldRow label="Playoff Format" muted={muted} text={text}>
        <div className={`px-3 py-2 rounded-lg ${inputBase} text-sm font-display font-bold uppercase tracking-wide`} data-testid="settings-playoff-format-display">
          {playoffTeams === 4 ? "4-team · 2 weeks" : playoffTeams === 6 ? "6-team · top 2 byes · 3 weeks" : "8-team · 3 weeks"}
        </div>
      </FieldRow>
      <FieldRow label="Consolation Bracket" muted={muted} text={text}>
        <ToggleField
          value={draft.consolation_bracket ?? true}
          onChange={(v) => set("consolation_bracket", v)}
          inputBase={inputBase} testid="settings-consolation-bracket"
          onLabel="On - non-playoff teams play through" offLabel="Off - eliminated teams stop"
        />
      </FieldRow>
      <FieldRow label="3rd-Place Game" muted={muted} text={text}>
        <ToggleField
          value={draft.third_place_game ?? true}
          onChange={(v) => set("third_place_game", v)}
          inputBase={inputBase} testid="settings-third-place-game"
          onLabel="On - semifinal losers compete" offLabel="Off"
        />
      </FieldRow>
    </div>
  );
}

function DivisionsTab({ draft, set, seasonStarted, league, muted, text, inputBase }) {
  const divisions = parseInt(draft.divisions ?? 0, 10) || 0;
  const divisionNames = draft.division_names || {};
  const teamCount = league?.num_teams || 12;

  // Validation warnings (real-time)
  const warnings = [];
  if (divisions > 0) {
    if (teamCount % divisions !== 0) {
      warnings.push(`Team count (${teamCount}) must divide evenly into ${divisions} divisions.`);
    }
    const pt = parseInt(draft.playoff_teams ?? 6, 10) || 6;
    if (pt < divisions) {
      warnings.push(`Playoff teams (${pt}) must be >= divisions (${divisions}).`);
    }
  }

  const setDivName = (idx, name) => {
    const id = `D${idx + 1}`;
    const next = { ...divisionNames, [id]: name };
    set("division_names", next);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <FieldRow label="Number of Divisions" locked={seasonStarted} lockReason="Locks at season start" muted={muted} text={text}>
        <SelectField
          value={String(divisions)}
          onChange={(v) => set("divisions", parseInt(v, 10) || 0)}
          locked={seasonStarted} inputBase={inputBase} testid="settings-divisions"
          options={[
            { value: "0", label: "None (single league)" },
            { value: "2", label: "2 divisions" },
            { value: "4", label: "4 divisions" },
          ]}
        />
      </FieldRow>
      <div /> {/* spacer */}
      {divisions > 0 && Array.from({ length: divisions }, (_, idx) => {
        const id = `D${idx + 1}`;
        const defaultName = `Division ${idx + 1}`;
        return (
          <FieldRow key={id} label={`Division ${idx + 1} Name`} locked={seasonStarted} muted={muted} text={text}>
            <input
              type="text"
              value={divisionNames[id] || ""}
              placeholder={defaultName}
              onChange={(e) => setDivName(idx, e.target.value)}
              disabled={seasonStarted}
              className={`w-full px-3 py-2 rounded-lg border ${inputBase} text-sm transition-all disabled:opacity-50`}
              data-testid={`settings-division-name-${idx}`}
            />
          </FieldRow>
        );
      })}
      {warnings.length > 0 && (
        <div className="md:col-span-2 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-400 text-xs space-y-1" data-testid="settings-division-warnings">
          {warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
        </div>
      )}
    </div>
  );
}

function ToggleField({ value, onChange, inputBase, testid, onLabel, offLabel }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`w-full px-3 py-2 rounded-lg border ${inputBase} text-sm transition-all flex items-center justify-between`}
      data-testid={testid}
    >
      <span className="font-display font-bold uppercase tracking-wide text-xs">
        {value ? onLabel : offLabel}
      </span>
      <span className={`relative inline-block w-10 h-5 rounded-full transition-colors ${value ? "bg-[#00C853]" : "bg-[#404040]"}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${value ? "translate-x-5" : ""}`} />
      </span>
    </button>
  );
}

// ── [Phase 2c-D §6.7 NEW-9] Danger Zone tab - Mock-only league deletion ─────
// Mirrors HF-3's MockDraftControls delete-confirmation pattern but lives in
// the Settings UI for post-draft mock leagues. Gated to MOCK_DRAFT_* and
// NO_DRAFT_MOCK_SEASON via league_type. Live leagues see a notice instead.
function DangerZoneTab({ league, muted, text }) {
  const [confirmTxt, setConfirmTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const nav = useNavigate();
  const lt = (league?.settings || {}).league_type || "";
  const isMock = lt.startsWith("MOCK_DRAFT") || lt === "NO_DRAFT_MOCK_SEASON";
  const expected = league?.name || "";
  const enabled = isMock && confirmTxt.trim() === expected && !busy;

  const handleDelete = async () => {
    if (!enabled) return;
    setBusy(true);
    setError(null);
    try {
      await axios.delete(`${API}/simulator/${league.id}`, { headers: authHeaders() });
      nav("/dashboard");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not delete league.");
      setBusy(false);
    }
  };

  if (!isMock) {
    return (
      <div className="space-y-4 max-w-2xl" data-testid="danger-zone-live-notice">
        <div className="flex gap-3 rounded-xl border border-yellow-500/40 bg-yellow-500/[0.06] p-4">
          <AlertCircle className="text-yellow-500 shrink-0 mt-0.5" size={18} />
          <div>
            <p className={`${text} font-display font-bold text-sm`}>Live leagues cannot be deleted self-service.</p>
            <p className={`${muted} text-sm mt-1`}>
              Real-money or multi-manager leagues require a commissioner-support escalation. Mock leagues you create alone can be deleted here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-2xl" data-testid="danger-zone-tab">
      <div>
        <h2 className={`${text} font-display font-black text-xl uppercase tracking-wide`}>Delete this Mock League</h2>
        <p className={`${muted} text-sm mt-1`}>
          Permanently removes the league, its draft session, its rosters, its season state, and any cached activity. This cannot be undone.
        </p>
      </div>
      <div className="rounded-xl border border-[#FF4444]/40 bg-[#FF4444]/[0.05] p-4 space-y-3">
        <div className="flex gap-3">
          <AlertCircle className="text-[#FF4444] shrink-0 mt-0.5" size={18} />
          <p className={`${text} text-sm`}>
            Type the league name <span className="font-display font-black">"{expected}"</span> below to confirm.
          </p>
        </div>
        <input
          type="text"
          value={confirmTxt}
          onChange={(e) => setConfirmTxt(e.target.value)}
          data-testid="danger-zone-confirm-input"
          placeholder={expected}
          className="w-full bg-[#1C1C1C] border border-[#252525] focus:border-[#FF4444] rounded-lg px-3 py-2 text-sm text-[#F5F5F5] outline-none transition-colors"
        />
        <button
          type="button"
          onClick={handleDelete}
          disabled={!enabled}
          data-testid="danger-zone-delete-btn"
          className={`w-full py-2.5 rounded-lg font-display font-black text-xs uppercase tracking-widest transition-all ${
            enabled
              ? "bg-[#FF4444] hover:bg-[#FF6666] text-white"
              : "bg-[#252525] text-[#666] cursor-not-allowed"
          }`}
        >
          {busy ? "Deleting..." : "Delete League Permanently"}
        </button>
        {error && <p className="text-[11px] text-[#FF4444]" data-testid="danger-zone-error">{error}</p>}
      </div>
    </div>
  );
}
